import type { McpConnection, McpConnectionList } from '@easyhelp/shared';
import { EmptyState } from '@easydoctor/ui';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { IconPlugConnectedX } from '@tabler/icons-react';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { ApiError } from '../../api/client';
import { SECONDARY_BUTTON_SX, TARGET_SX } from '../accessible-buttons';
import { ConfirmDialog } from '../ConfirmDialog';
import { formatDateTime } from '../ticket';
import { clientName, clientOrigin } from './describe';

/** Di chi sono le connessioni: le proprie (profilo) o di un membro (Impostazioni › Team). */
export type McpConnectionsOwner = { kind: 'own' } | { kind: 'member'; name: string };

export interface McpConnectionsPanelProps {
  owner: McpConnectionsOwner;
  /** Elenco delle connessioni attive (`useOwnMcpConnections` o `useMemberMcpConnections`). */
  query: UseQueryResult<McpConnectionList, ApiError>;
  /** Revoca con l'id della connessione (`useRevokeOwnMcpConnection` o `useRevokeMemberMcpConnection`). */
  revoke: UseMutationResult<void, ApiError, string>;
  /** Esito della revoca da annunciare (lo Snackbar della pagina). */
  onRevoked: (message: string) => void;
}

/** Codici per cui la revoca non ha più senso: resta solo «Chiudi» e gli elenchi si rileggono. */
const BLOCKING_CODES: ReadonlySet<string | null> = new Set(['mcp.connection_not_found', 'team.member_not_found']);

/** Nome della lista e testi che cambiano con il proprietario. */
function ownerTexts(owner: McpConnectionsOwner) {
  return owner.kind === 'own'
    ? {
        listLabel: 'Le tue connessioni di Claude e Cursor',
        empty: 'Non hai collegato Claude né Cursor',
        emptyHint: 'Quando colleghi EasyHelp a un client e premi «Consenti», la connessione compare qui.',
        onBehalf: 'a tuo nome',
        after: 'Le tue sessioni della console e le altre connessioni restano aperte.',
      }
    : {
        listLabel: `Connessioni di Claude e Cursor di ${owner.name}`,
        empty: `${owner.name} non ha collegato Claude né Cursor`,
        emptyHint: undefined,
        onBehalf: `a nome di ${owner.name}`,
        after: `Le sessioni della console di ${owner.name} e le sue altre connessioni restano aperte.`,
      };
}

/**
 * Connessioni MCP attive di un membro (PRD-007 US-2, #152), comune a Impostazioni › Team e al
 * profilo: client (nome dichiarato e da dove si è collegato), data di collegamento, ultimo uso e
 * scadenza, con «Revoca» e la sua conferma. Gestisce caricamento, errore con «Riprova» e stato vuoto.
 * Dopo la revoca il client riceve un errore alla richiesta successiva e chiede un nuovo collegamento.
 * La riga revocata sparisce: il focus va sul pannello (WCAG 2.4.3), non resta su un comando rimosso.
 */
export function McpConnectionsPanel({ owner, query, revoke, onRevoked }: McpConnectionsPanelProps) {
  const [target, setTarget] = useState<McpConnection | null>(null);
  const [revokedCount, setRevokedCount] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const texts = ownerTexts(owner);

  // Dopo che il dialogo è smontato e ha riportato il focus su «Revoca», di una riga che la rilettura
  // dell'elenco sta per togliere.
  useEffect(() => {
    if (revokedCount > 0) rootRef.current?.focus();
  }, [revokedCount]);

  const close = () => {
    setTarget(null);
    revoke.reset();
  };

  const confirm = () => {
    if (!target) return;
    const name = clientName(target);
    revoke.mutate(target.id, {
      onSuccess: () => {
        close();
        setRevokedCount((count) => count + 1);
        onRevoked(`Connessione di ${name} revocata: alla prossima richiesta ${name} dovrà essere collegato di nuovo.`);
      },
    });
  };

  if (query.isPending) {
    return (
      <Stack direction="row" spacing={2} alignItems="center" role="status">
        <CircularProgress size={20} />
        <Typography>Caricamento delle connessioni…</Typography>
      </Stack>
    );
  }

  if (query.isError) {
    return (
      <Alert
        severity="error"
        // `error.darker`: il colore standard dell'Alert di MUI arriva a 6,7:1 (AAA chiede 7:1, PAGE-011).
        sx={{ color: 'error.darker' }}
        action={
          <Button color="inherit" onClick={() => void query.refetch()} sx={TARGET_SX}>
            Riprova
          </Button>
        }
      >
        <AlertTitle>Connessioni non disponibili</AlertTitle>
        {query.error.message}
      </Alert>
    );
  }

  const { connections } = query.data;

  return (
    // Destinazione del focus dopo una revoca (tabIndex -1: non entra nell'ordine di tabulazione).
    <Box ref={rootRef} tabIndex={-1} sx={{ '&:focus': { outline: 'none' } }}>
      {connections.length === 0 ? (
        <EmptyState
          variant="plain"
          icon={<IconPlugConnectedX />}
          title={texts.empty}
          description={texts.emptyHint}
          sx={{ py: 3 }}
        />
      ) : (
        <List disablePadding aria-label={texts.listLabel}>
          {connections.map((connection) => {
            const name = clientName(connection);
            const origin = clientOrigin(connection);
            const createdAt = formatDateTime(connection.createdAt);
            return (
              <ListItem key={connection.id} divider sx={{ gap: 3, flexWrap: 'wrap', px: 0 }}>
                <ListItemText
                  primary={
                    <Typography component="span" fontWeight={500}>
                      {name}{' '}
                      <Typography component="span" color="text.secondary">
                        · {origin}
                      </Typography>
                    </Typography>
                  }
                  secondary={
                    <>
                      {`Collegato il ${createdAt}`}
                      {' · '}
                      {connection.lastUsedAt ? `ultimo uso ${formatDateTime(connection.lastUsedAt)}` : 'mai usato'}
                      {' · '}
                      {`scade il ${formatDateTime(connection.expiresAt)}`}
                    </>
                  }
                  slotProps={{ secondary: { color: 'text.secondary' } }}
                  sx={{ flex: '1 1 260px', m: 0 }}
                />
                <Button
                  variant="outlined"
                  // Il nome comincia con l'etichetta visibile (WCAG 2.5.3) e dice quale connessione.
                  aria-label={`Revoca ${name} (${origin}), collegato il ${createdAt}`}
                  onClick={() => setTarget(connection)}
                  sx={{ ...SECONDARY_BUTTON_SX, flexShrink: 0 }}
                >
                  Revoca
                </Button>
              </ListItem>
            );
          })}
        </List>
      )}

      {target && (
        <ConfirmDialog
          open
          title={`Revocare la connessione di ${clientName(target)}?`}
          confirmLabel="Revoca"
          confirmColor="error"
          error={revoke.error?.message}
          blocked={BLOCKING_CODES.has(revoke.error?.code ?? null)}
          busy={revoke.isPending}
          onConfirm={confirm}
          onClose={close}
        >
          {clientName(target)} ({clientOrigin(target)}) smette subito di lavorare {texts.onBehalf}: alla prossima
          richiesta riceve un errore e chiede di collegarsi di nuovo. {texts.after}
        </ConfirmDialog>
      )}
    </Box>
  );
}
